/**
 * admin/src/pages/BlogEditorPage.tsx
 * Tạo / Sửa bài viết blog — 2-column layout
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useBlogPost, useBlogMutations } from '../hooks/useBlog';
import { ErrorBanner, Toggle } from '../components/ui/index';

// ─── Slug helper ─────────────────────────────────────────────────────────────
function toSlug(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')   // remove diacritics
    .replace(/đ/g, 'd')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

// ─── Form State ───────────────────────────────────────────────────────────────
interface FormState {
  title: string;
  slug: string;
  description: string;
  category: string;
  tab: string;
  author: string;
  tags: string;         // comma-separated
  cover_image: string;
  reading_time: string; // string for input, parse to number on save
  is_featured: boolean;
  body: string;
}

const DEFAULT_FORM: FormState = {
  title: '',
  slug: '',
  description: '',
  category: 'tieng-anh',
  tab: 'students',
  author: 'Đội ngũ Luyện Thi',
  tags: '',
  cover_image: '',
  reading_time: '5',
  is_featured: false,
  body: '',
};

export function BlogEditorPage() {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();
  const isEditMode = !!slug;

  // Load existing post in edit mode
  const { post, isLoading: isLoadingPost, error: loadError } = useBlogPost(slug);
  const { createPost, updatePost, isSaving } = useBlogMutations();

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [slugAutoGenerated, setSlugAutoGenerated] = useState(!isEditMode);

  // Populate form when post loads (edit mode)
  useEffect(() => {
    if (isEditMode && post?.meta) {
      const meta = post.meta;
      setForm({
        title: meta.title ?? '',
        slug: meta.slug ?? '',
        description: meta.description ?? '',
        category: meta.category ?? 'tieng-anh',
        tab: meta.tab ?? 'students',
        author: meta.author ?? 'Đội ngũ Luyện Thi',
        tags: (meta.tags ?? []).join(', '),
        cover_image: meta.cover_image ?? '',
        reading_time: String(meta.reading_time ?? 5),
        is_featured: !!meta.is_featured,
        body: post.body ?? '',
      });
      setSlugAutoGenerated(false);
    }
  }, [isEditMode, post]);

  // Auto-generate slug from title in create mode
  function handleTitleChange(v: string) {
    setForm((f) => ({
      ...f,
      title: v,
      slug: slugAutoGenerated ? toSlug(v) : f.slug,
    }));
  }

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSave() {
    const tagArr = form.tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    const payload = {
      title: form.title,
      slug: form.slug || undefined,
      description: form.description,
      category: form.category,
      tab: form.tab,
      author: form.author,
      tags: tagArr,
      cover_image: form.cover_image || undefined,
      reading_time: parseInt(form.reading_time, 10) || 5,
      is_featured: form.is_featured,
      body: form.body,
    };

    if (isEditMode && slug) {
      const ok = await updatePost(slug, payload);
      if (ok) navigate('/admin/blog');
    } else {
      const newSlug = await createPost(payload);
      if (newSlug) navigate('/admin/blog');
    }
  }

  // ─── Loading / Error state ────────────────────────────────────────────────
  if (isEditMode && isLoadingPost) {
    return (
      <div className="flex-center" style={{ height: 300, color: 'var(--text-3)' }}>
        ⏳ Đang tải bài viết...
      </div>
    );
  }

  if (isEditMode && loadError) {
    return (
      <div style={{ padding: 24 }}>
        <ErrorBanner message={loadError} />
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Toolbar */}
      <div className="page-header">
        <div className="page-header__left" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Link to="/admin/blog" className="btn btn-ghost btn-sm">
            ← Quay lại
          </Link>
          <h2 style={{ margin: 0, fontSize: 20 }}>
            {isEditMode ? `Sửa: ${slug}` : 'Tạo bài viết mới'}
          </h2>
        </div>
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={isSaving || !form.title.trim()}
        >
          {isSaving ? '⏳ Đang lưu...' : '💾 Lưu bài viết'}
        </button>
      </div>

      {/* 2-column grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: 24,
          alignItems: 'start',
        }}
      >
        {/* LEFT — Main content */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Title */}
          <div className="card">
            <div className="form-group">
              <label className="form-label" htmlFor="blog-title">
                Tiêu đề <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <input
                id="blog-title"
                className="input"
                type="text"
                placeholder="Nhập tiêu đề bài viết..."
                value={form.title}
                onChange={(e) => handleTitleChange(e.target.value)}
              />
            </div>

            {/* Slug */}
            <div className="form-group" style={{ marginTop: 12 }}>
              <label className="form-label" htmlFor="blog-slug">
                Slug (URL)
              </label>
              <input
                id="blog-slug"
                className="input"
                type="text"
                placeholder="ten-bai-viet-cua-ban"
                value={form.slug}
                readOnly={isEditMode}
                onChange={(e) => {
                  setSlugAutoGenerated(false);
                  setField('slug', e.target.value);
                }}
                style={isEditMode ? { opacity: 0.7 } : undefined}
              />
              {isEditMode && (
                <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-3)' }}>
                  Slug không thể thay đổi sau khi tạo (URL cố định)
                </p>
              )}
              {!isEditMode && (
                <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-3)' }}>
                  Tự động tạo từ tiêu đề. Có thể chỉnh sửa thủ công.
                </p>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="card">
            <div className="form-group">
              <label className="form-label" htmlFor="blog-desc">
                Mô tả ngắn
              </label>
              <textarea
                id="blog-desc"
                className="input"
                rows={2}
                placeholder="Mô tả ngắn hiển thị ở listing, SEO description..."
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
              />
            </div>
          </div>

          {/* Body / Content */}
          <div className="card">
            <div className="form-group">
              <label className="form-label" htmlFor="blog-body">
                Nội dung (Markdown)
              </label>
              <textarea
                id="blog-body"
                className="input"
                placeholder="# Tiêu đề&#10;&#10;Viết nội dung bài viết bằng Markdown..."
                value={form.body}
                onChange={(e) => setField('body', e.target.value)}
                style={{ minHeight: 400, fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6 }}
              />
            </div>
          </div>
        </div>

        {/* RIGHT — Meta */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-2)', marginBottom: 12 }}>
              Phân loại
            </div>

            {/* Category */}
            <div className="form-group">
              <label className="form-label" htmlFor="blog-category">Category</label>
              <select
                id="blog-category"
                className="input"
                value={form.category}
                onChange={(e) => setField('category', e.target.value)}
              >
                <option value="toan">Toán</option>
                <option value="tieng-anh">Tiếng Anh</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Tab */}
            <div className="form-group" style={{ marginTop: 12 }}>
              <label className="form-label" htmlFor="blog-tab">Tab đối tượng</label>
              <select
                id="blog-tab"
                className="input"
                value={form.tab}
                onChange={(e) => setField('tab', e.target.value)}
              >
                <option value="students">Học sinh</option>
                <option value="parents">Phụ huynh</option>
                <option value="teachers">Giáo viên</option>
              </select>
            </div>

            {/* Author */}
            <div className="form-group" style={{ marginTop: 12 }}>
              <label className="form-label" htmlFor="blog-author">Tác giả</label>
              <input
                id="blog-author"
                className="input"
                type="text"
                value={form.author}
                onChange={(e) => setField('author', e.target.value)}
              />
            </div>

            {/* Tags */}
            <div className="form-group" style={{ marginTop: 12 }}>
              <label className="form-label" htmlFor="blog-tags">Tags</label>
              <input
                id="blog-tags"
                className="input"
                type="text"
                placeholder="VD: #Cambridge, #Flyers"
                value={form.tags}
                onChange={(e) => setField('tags', e.target.value)}
              />
              <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-3)' }}>
                Phân cách bằng dấu phẩy
              </p>
            </div>
          </div>

          {/* Media & Display */}
          <div className="card">
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-2)', marginBottom: 12 }}>
              Media & Hiển thị
            </div>

            {/* Cover Image */}
            <div className="form-group">
              <label className="form-label" htmlFor="blog-cover">Cover Image URL</label>
              <input
                id="blog-cover"
                className="input"
                type="text"
                placeholder="https://..."
                value={form.cover_image}
                onChange={(e) => setField('cover_image', e.target.value)}
              />
              {form.cover_image && (
                <img
                  src={form.cover_image}
                  alt="preview"
                  style={{
                    marginTop: 8,
                    width: '100%',
                    maxHeight: 120,
                    objectFit: 'cover',
                    borderRadius: 6,
                    border: '1px solid var(--border)',
                  }}
                />
              )}
            </div>

            {/* Reading Time */}
            <div className="form-group" style={{ marginTop: 12 }}>
              <label className="form-label" htmlFor="blog-reading-time">
                Thời gian đọc (phút)
              </label>
              <input
                id="blog-reading-time"
                className="input"
                type="number"
                min={1}
                max={60}
                value={form.reading_time}
                onChange={(e) => setField('reading_time', e.target.value)}
              />
            </div>

            {/* Is Featured */}
            <div className="form-group" style={{ marginTop: 12 }}>
              <label className="form-label">Nổi bật (Featured)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <Toggle
                  checked={form.is_featured}
                  onChange={(v) => setField('is_featured', v)}
                  label={form.is_featured ? 'Có — hiển thị featured' : 'Không'}
                />
              </div>
            </div>
          </div>

          {/* Save shortcut in sidebar */}
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving || !form.title.trim()}
            style={{ width: '100%' }}
          >
            {isSaving ? '⏳ Đang lưu...' : '💾 Lưu bài viết'}
          </button>
        </div>
      </div>
    </div>
  );
}
